import {
  createGroup,
  updateGroup,
  deleteGroup,
  updatedCOAStructure,
  recategoriseAccount,
  setSwitchRuleForAccount,
  deleteSwitchRuleForAccount
} from '../../predict/src/core/api/chart-of-accounts';
import {
  CreateGroupPayload,
  COAUpdateFullStructurePayload,
  COARecategoriseAccountPayload,
  COASwitchRulePayload
} from '../../predict/src/chart-of-accounts/types';

import { createMutation } from '../utils/mutations';
import { chartOfAccountsQueryKey } from '../queries/chartOfAccounts';
import { queryClient } from '../core/queryClient';
import { COAGroup } from '@futrli-sdk/predict';

export type CreateGroupInput = Partial<CreateGroupPayload>;
export interface UpdateGroupInput {
  groupId: string;
  data: Partial<CreateGroupPayload>;
}
export type DeleteGroupInput = string;
export type UpdateCOAStructureInput = COAUpdateFullStructurePayload;
export type RecategoriseAccountInput = COARecategoriseAccountPayload;
export interface SetSwitchRuleInput {
  accountId: string;
  data: COASwitchRulePayload;
}
export type DeleteSwitchRuleInput = string;

type CreateGroupResult = Awaited<ReturnType<typeof createGroup>>;
type UpdateGroupResult = Awaited<ReturnType<typeof updateGroup>>;
type DeleteGroupResult = Awaited<ReturnType<typeof deleteGroup>>;
type UpdateCOAStructureResult = Awaited<ReturnType<typeof updatedCOAStructure>>;
type RecategoriseAccountResult = Awaited<ReturnType<typeof recategoriseAccount>>;
type SetSwitchRuleResult = Awaited<ReturnType<typeof setSwitchRuleForAccount>>;
type DeleteSwitchRuleResult = Awaited<ReturnType<typeof deleteSwitchRuleForAccount>>;

export interface CreateGroupAndUpdateStructureInput {
  groupData: CreateGroupInput;
  buildStructurePayload: (newGroup: COAGroup) => UpdateCOAStructureInput;
}

export const createGroupAndUpdateStructureMutation = (workspaceId: string, organisationId: string) =>
  createMutation<CreateGroupAndUpdateStructureInput, CreateGroupResult>({
    mutationFn: async input => {
      const newGroup = await createGroup(workspaceId, organisationId, input.groupData);

      const structurePayload = input.buildStructurePayload(newGroup);

      await updatedCOAStructure(workspaceId, organisationId, structurePayload);

      return newGroup;
    },
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: chartOfAccountsQueryKey(organisationId, workspaceId) });
    }
  });

export const updateGroupMutation = (workspaceId: string, organisationId: string) =>
  createMutation<UpdateGroupInput, UpdateGroupResult>({
    mutationFn: input => updateGroup(workspaceId, organisationId, input.groupId, input.data)
  });

export const deleteGroupMutation = (workspaceId: string, organisationId: string) =>
  createMutation<DeleteGroupInput, DeleteGroupResult>({
    mutationFn: groupId => deleteGroup(workspaceId, organisationId, groupId),
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: chartOfAccountsQueryKey(organisationId, workspaceId) });
    }
  });

export const updateCOAStructureMutation = (workspaceId: string, organisationId: string) =>
  createMutation<UpdateCOAStructureInput, UpdateCOAStructureResult>({
    mutationFn: data => updatedCOAStructure(workspaceId, organisationId, data),
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: chartOfAccountsQueryKey(organisationId, workspaceId) });
    }
  });

export const recategoriseAccountMutation = (workspaceId: string, organisationId: string) =>
  createMutation<RecategoriseAccountInput, RecategoriseAccountResult>({
    mutationFn: data => recategoriseAccount(workspaceId, organisationId, data),
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: chartOfAccountsQueryKey(organisationId, workspaceId) });
    }
  });

export const setSwitchRuleMutation = (workspaceId: string, organisationId: string) =>
  createMutation<SetSwitchRuleInput, SetSwitchRuleResult>({
    mutationFn: input => setSwitchRuleForAccount(workspaceId, organisationId, input.accountId, input.data),
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: chartOfAccountsQueryKey(organisationId, workspaceId) });
    }
  });

export const deleteSwitchRuleMutation = (workspaceId: string, organisationId: string) =>
  createMutation<DeleteSwitchRuleInput, DeleteSwitchRuleResult>({
    mutationFn: accountId => deleteSwitchRuleForAccount(workspaceId, organisationId, accountId),
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: chartOfAccountsQueryKey(organisationId, workspaceId) });
    }
  });
